import { useState, useCallback } from 'react';
import { api, AdminStripeEvent, AdminStripeEventListParams } from '@/lib/api';

export function StripeEventsTab() {
  const [filters, setFilters] = useState<AdminStripeEventListParams>({ limit: 25 });
  const [events, setEvents] = useState<AdminStripeEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  // Expanded event
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [expandedPayload, setExpandedPayload] = useState<unknown>(null);
  const [payloadLoading, setPayloadLoading] = useState(false);

  const search = useCallback(
    async (offset = 0) => {
      setLoading(true);
      setError(null);
      try {
        const result = await api.adminListStripeEvents({ ...filters, offset });
        setEvents(result.events);
        setTotal(result.total);
        setFilters((f) => ({ ...f, offset }));
        setSearched(true);
        setExpandedEventId(null);
        setExpandedPayload(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed');
      } finally {
        setLoading(false);
      }
    },
    [filters],
  );

  const limit = filters.limit ?? 25;
  const offset = filters.offset ?? 0;
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  const toggleExpand = async (eventId: string) => {
    if (expandedEventId === eventId) {
      setExpandedEventId(null);
      setExpandedPayload(null);
      return;
    }

    setExpandedEventId(eventId);
    setPayloadLoading(true);
    try {
      const detail = await api.adminGetStripeEvent(eventId);
      setExpandedPayload(detail.payload);
    } catch {
      setExpandedPayload({ error: 'Failed to load event payload' });
    } finally {
      setPayloadLoading(false);
    }
  };

  return (
    <div>
      {/* Filters */}
      <div className="bg-card rounded-lg border border-border p-4 mb-6">
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Event Type</label>
            <input
              type="text"
              value={filters.eventType || ''}
              onChange={(e) =>
                setFilters((f) => ({ ...f, eventType: e.target.value || undefined }))
              }
              placeholder="e.g. checkout, invoice"
              className="w-48 px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Start Date</label>
            <input
              type="date"
              value={filters.startDate || ''}
              onChange={(e) =>
                setFilters((f) => ({ ...f, startDate: e.target.value || undefined }))
              }
              className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">End Date</label>
            <input
              type="date"
              value={filters.endDate || ''}
              onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value || undefined }))}
              className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Status</label>
            <select
              value={filters.processedOk === undefined ? '' : String(filters.processedOk)}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  processedOk: e.target.value === '' ? undefined : e.target.value === 'true',
                }))
              }
              className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
            >
              <option value="">All</option>
              <option value="true">Succeeded</option>
              <option value="false">Failed</option>
            </select>
          </div>
          <button
            onClick={() => search(0)}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Search'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {error}
        </div>
      )}

      {searched && (
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              {total} event{total !== 1 ? 's' : ''}
            </h3>
          </div>

          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground">No events match your filters</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 text-muted-foreground font-medium">Event ID</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Type</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Received</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Status</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((evt) => (
                      <>
                        <tr
                          key={evt.eventId}
                          onClick={() => toggleExpand(evt.eventId)}
                          className={`border-b border-border/50 cursor-pointer hover:bg-muted/50 ${
                            evt.processedOk === false ? 'bg-destructive/5' : ''
                          }`}
                        >
                          <td className="py-2 text-foreground font-mono text-xs">{evt.eventId}</td>
                          <td className="py-2 text-foreground">{evt.eventType}</td>
                          <td className="py-2 text-muted-foreground">
                            {new Date(evt.receivedAt).toLocaleString()}
                          </td>
                          <td className="py-2">
                            {evt.processedOk === true && (
                              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                OK
                              </span>
                            )}
                            {evt.processedOk === false && (
                              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                                Failed
                              </span>
                            )}
                            {evt.processedOk === null && (
                              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                                Pending
                              </span>
                            )}
                          </td>
                          <td className="py-2 text-destructive text-xs truncate max-w-[200px]">
                            {evt.errorMessage || '-'}
                          </td>
                        </tr>
                        {expandedEventId === evt.eventId && (
                          <tr key={`${evt.eventId}-payload`}>
                            <td colSpan={5} className="p-4 bg-muted/30">
                              {payloadLoading ? (
                                <p className="text-sm text-muted-foreground">Loading payload...</p>
                              ) : (
                                <pre className="text-xs text-foreground overflow-auto max-h-96 p-3 bg-background rounded-lg border border-border">
                                  {JSON.stringify(expandedPayload, null, 2)}
                                </pre>
                              )}
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => search(Math.max(0, offset - limit))}
                      disabled={offset === 0 || loading}
                      className="px-3 py-1.5 text-sm bg-muted text-muted-foreground hover:bg-accent rounded-md disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => search(offset + limit)}
                      disabled={offset + limit >= total || loading}
                      className="px-3 py-1.5 text-sm bg-muted text-muted-foreground hover:bg-accent rounded-md disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
